import {
  APIErrorStatus,
  ListingType,
  ModalName,
  PaymentGatewayName,
} from '@share/enums';
import { AxiosError } from 'axios';
import { BackendResponse } from '@share/interfaces';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import {
  handleInputMaxLength,
  handleInputNumberOnly,
  showPercentage,
  toggleBootstrapModal,
} from '@util/helper';
import { market } from '@state/market';
import { toast } from 'react-toastify';
import { useAPI, useCurrency, useHelper } from 'src/hooks';
import { useDebouncedCallback } from 'use-debounce';
import { useForm } from 'react-hook-form';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { web3 } from '@state/web3';
import Agreement from './Agreement';
import CheckoutBidBuyFrame from '@com/payment/CheckoutBidBuyFrame';
import DotLoading from '@com/utils/DotLoading';
import ErrorMsg from '@com/utils/ErrorMsg';
import GkashPaymentBtn from '@com/buttons/GkashPaymentBtn';
import PaymentRow from '@com/utils/PaymentRow';

interface Params {
  tokenId: number;
  collectionAddress: string;
  contractChainId: number;
  paymentGateway: PaymentGatewayName;
  orderId: number;
  currencyId: number;
  basePrice: number;
  loadLaunchpadData: () => void;
}

interface FormInputs {
  bidPrice: number;
  check: boolean;
}

const BidModal = forwardRef(
  (
    {
      tokenId,
      collectionAddress,
      contractChainId,
      orderId,
      currencyId,
      paymentGateway,
      basePrice: _basePrice,
      loadLaunchpadData,
    }: Params,
    ref
  ) => {
    /* Define state / hook */
    const { getCurrencyNameById, isFiatCurrency, getMinimumUnit } =
      useCurrency();
    const { isSigned, getBalance, makeBid, payment } = market.useContainer();
    const { getAddress } = web3.useContainer();
    const [isShow, setIsShow] = useState<boolean>();
    const [balance, setBalance] = useState<string>(String(0));
    const [isLoading, setIsLoading] = useState(false);
    const [isChecked, setIsChecked] = useState(false);
    const { t } = useTranslation();
    const { displayCurrency } = useHelper();
    const { getGasReport, processGkashBid } = useAPI();

    const {
      register,
      formState: { errors },
      handleSubmit,
      watch,
    } = useForm<FormInputs>({ mode: 'onBlur' }); // Required since no submit button

    const isFiat = isFiatCurrency(currencyId) || false;
    const { data } = useQuery(
      [getGasReport.name, orderId],
      () => getGasReport(orderId),
      {
        enabled: isFiat && isShow === true, // Prevent undefined also execute the query
        refetchInterval: 2 * 60 * 1000,
        retry: false,
        onError(error: AxiosError<BackendResponse<undefined>>) {
          if (error.response?.data?.code === APIErrorStatus.ORDER_NOT_ACTIVE) {
            loadLaunchpadData();
          }
          setTimeout(() => setIsShow(false), 500);
        },
      }
    );

    /* Custom Data Object for render or other usages */
    const currencyName = getCurrencyNameById(currencyId);

    const bidPrice = Number(watch('bidPrice'));

    const basePrice = bidPrice || _basePrice || 0;

    const transactionFee = Number(data?.base_gas) || 0;

    // TODO: Based on creator preference
    const handlingFee = 0;
    // const handlingFee =
    //   ((subtotalFee + transactionFee) *
    //     Number(data?.service?.handling_fee || 0)) /
    //   100;

    const totalPrice = Number(
      (basePrice + transactionFee + handlingFee).toFixed(
        // Here used a hacky way to get the total decimal place have to display
        String(getMinimumUnit(currencyId)).split('0').length - 1
      )
    );

    /* render control function / logic */
    const toggleModal = useDebouncedCallback(
      async () => {
        // If crypto
        if (!isFiatCurrency(currencyId)) {
          setBalance(await getBalance(currencyId));
        }

        if (!isLoading) setIsShow(!isShow);
      },
      400,
      { leading: true, trailing: true }
    );

    const handleCryptoBid = useDebouncedCallback(
      async (formData: FormInputs) => {
        const isLogin = await payment();
        if (!isLogin) return;

        setIsLoading(true);
        await makeBid(
          collectionAddress,
          tokenId,
          currencyId,
          formData.bidPrice
        );
        setIsLoading(false);

        toggleModal();
        /**
         * FIXME: If not adding this delay, the modal will not toggle when
         * 'Disabled Cache' is not enable in devtools...
         * Also for firefox only ~150ms need, but for chrome has to ~350ms
         * so use 500ms for safe
         */
        setTimeout(() => setIsShow(false), 500);
        loadLaunchpadData();
      },
      500,
      { leading: true, trailing: false }
    );

    const handleGkashBid = async () => {
      if (!isChecked) {
        toast.info(t('toastMessage.checkTerms'));
        return null;
      }

      const isLoginDoubleCheck = await payment();
      if (!isLoginDoubleCheck) return null;
      if (!data) return null;

      return processGkashBid({
        chainId: contractChainId,
        collectionAddress,
        tokenId,
        bidPrice: basePrice,
        totalPrice,
        bidderAddress: getAddress(),
      });
    };

    /* Data Change Listeners - useEffect/useMemo/useCallback */
    useImperativeHandle(ref, () => ({ toggleModal }));

    useEffect(() => {
      if (isShow != null) toggleBootstrapModal(ModalName.BID);
    }, [isShow]);

    /* Styling */

    /* Render */
    return (
      <div
        aria-hidden
        className="modal fade popup"
        id={ModalName.BID}
        role="dialog"
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <button
              className="close"
              type="button"
              onClick={() => {
                toggleModal();
              }}
            >
              <span aria-hidden>&times;</span>
            </button>
            <div className="modal-body space-y-20 pd-40">
              <h3>{`${t('modal.placeBid')} ${tokenId}`}</h3>
              {isLoading ? (
                <div>
                  <DotLoading />
                  <div className="row mt-4">
                    <div className="h1 col-12 text-center mt-5">
                      {isSigned
                        ? t('modal.waitContract')
                        : t('modal.signContract')}
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <p className="mt-3">{t('modal.bidPrice')}</p>
                  <input
                    aria-required
                    aria-label={t('modal.bidForEachNFT')}
                    className="form-control mt-3"
                    id="bidPrice"
                    inputMode="decimal"
                    maxLength={12}
                    step={getMinimumUnit(currencyId)}
                    type="number"
                    onKeyDown={(e) =>
                      handleInputMaxLength(e) && handleInputNumberOnly(e)
                    }
                    {...register('bidPrice', {
                      valueAsNumber: true,
                      required: {
                        value: true,
                        message: t('modal.priceRequired'),
                      },
                      min: {
                        value: basePrice,
                        message: t('modal.checkMinBid'),
                      },
                      validate: {
                        value: (value) => {
                          const minimumUnit = getMinimumUnit(currencyId);
                          const digits = String(value).split('.')?.[1]?.length;
                          const maxDigits =
                            String(minimumUnit).split('.')[1]?.length;

                          return (
                            digits === undefined ||
                            digits <= maxDigits ||
                            t('modal.pricePatternRequired', {
                              min: minimumUnit,
                            })
                          );
                        },
                      },
                    })}
                  />
                  <ErrorMsg errors={errors} name="bidPrice" />
                  <div className="mt-3">
                    <PaymentRow
                      currency={currencyName}
                      price={_basePrice}
                      title={t('modal.bidAtLeastRequired')}
                    />
                    {isFiat && (
                      <>
                        <PaymentRow
                          currency={currencyName}
                          price={bidPrice || basePrice}
                          title={t('modal.subtotal')}
                        />
                        <PaymentRow
                          currency={currencyName}
                          price={data?.base_gas}
                          title={t('modal.transactionFee')}
                        />
                        <PaymentRow
                          currency={currencyName}
                          price={handlingFee}
                          title={t('modal.handlingFee', {
                            percentage: showPercentage(
                              Number(data?.service?.payment_fee)
                            ),
                          })}
                        />
                      </>
                    )}
                    <PaymentRow
                      isTotal
                      currency={currencyName}
                      price={totalPrice || basePrice} // fiat = total_price and crypto = price
                      title={t('modal.totalPaid')}
                    />
                    {isFiat && (
                      <Agreement
                        isChecked={isChecked}
                        setIsChecked={setIsChecked}
                      />
                    )}
                  </div>
                  {isFiat ? (
                    <h1>
                      {paymentGateway === PaymentGatewayName.CHECKOUT && (
                        <CheckoutBidBuyFrame
                          bidPrice={bidPrice}
                          checked={isChecked}
                          collectionAddress={collectionAddress}
                          contractChainId={contractChainId}
                          tokenId={tokenId}
                          totalPrice={totalPrice}
                          type={ListingType.BID}
                        />
                      )}
                      {paymentGateway === PaymentGatewayName.GKASH && (
                        <GkashPaymentBtn prepayHook={handleGkashBid} />
                      )}
                    </h1>
                  ) : (
                    <>
                      <div className="d-flex justify-content-between mt-5 mb-5">
                        <p>{t('modal.balanceInWallet')}</p>
                        <div className="text-right mt-1">
                          <div className="price color-popup">
                            {displayCurrency(balance, currencyName)}
                          </div>
                        </div>
                      </div>
                      <button
                        aria-label={t('modal.bidInCryto')}
                        className="btn btn-primary"
                        disabled={isLoading}
                        type="button"
                        onClick={handleSubmit(handleCryptoBid)}
                      >
                        {t('modal.bidInCryto')}
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
);

export default BidModal;
